// src/Home/Navbar.js
import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Typography,
  useMediaQuery,
  useTheme,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemText,
  Menu,
  Divider,
  MenuItem,
} from "@mui/material";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import AddIcon from "@mui/icons-material/Add";
import EmailIcon from "@mui/icons-material/Email";
import LanguageIcon from "@mui/icons-material/Language";
import HomeIcon from "@mui/icons-material/Home";
import InfoIcon from "@mui/icons-material/Info";
import ArticleIcon from "@mui/icons-material/Article";
import WorkOutlineIcon from "@mui/icons-material/WorkOutline";
import { motion } from "framer-motion";
import Logo from "./Images/Logo.png";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

const Navbar = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [langAnchorEl, setLangAnchorEl] = useState(null);
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const { t, i18n } = useTranslation();

  // Load saved language on mount and set dir accordingly
  useEffect(() => {
    const savedLang = localStorage.getItem("appLanguage");
    if (savedLang) {
      i18n.changeLanguage(savedLang);
      document.documentElement.dir = savedLang === "ar" ? "rtl" : "ltr";
    } else {
      // Set direction according to default language
      document.documentElement.dir = i18n.language === "ar" ? "rtl" : "ltr";
    }
  }, [i18n]);

  // Determine font family based on language direction
  const isRTL = i18n.dir() === "rtl";
  const fontFamily = isRTL ? "'Tajawal', Arial, sans-serif" : "Arial, Helvetica, sans-serif";

  const menuItems = [
    { label: t("home"), icon: <HomeIcon />, onClick: () => navigate("/") },
    { label: t("aboutUs"), icon: <InfoIcon />, onClick: () => navigate("/about") },
    { label: t("ourProjects"), icon: <WorkOutlineIcon />, onClick: () => navigate("/project") },
    { label: t("companyNews"), icon: <ArticleIcon />, onClick: () => navigate("/news") },
  ];

  const toggleDrawer = (open) => () => {
    setDrawerOpen(open);
  };

  const handleLanguageMenuOpen = (event) => {
    setLangAnchorEl(event.currentTarget);
  };

  const handleLanguageMenuClose = () => {
    setLangAnchorEl(null);
  };

  const changeLanguage = (lang) => {
    i18n.changeLanguage(lang);
    document.documentElement.dir = lang === "ar" ? "rtl" : "ltr";
    localStorage.setItem("appLanguage", lang); // Persist language
    handleLanguageMenuClose();
  };

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
      setIsCollapsed(window.scrollY > 50);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const collapsedVariants = {
    expanded: { width: "auto", opacity: 1 },
    collapsed: { width: 120, opacity: 1 },
  };

  return (
  <Box
  style={{
    marginTop: "20px",
    position: "fixed",
    width: "100%",
    zIndex: 1200,
    fontFamily, // apply font family here
   flexDirection: isRTL ? "row-reverse" : "row", // Set flexDirection based on RTL
          textAlign: isRTL ? "right" : "left", // Align text for RTL
  }}
>

      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          maxWidth: "95%",
          mx: "auto",
          flexWrap: "wrap",
          fontFamily, // apply font family here for fallback
        
        }}
      >
        {/* Left Section */}
        <motion.div
          initial="expanded"
          animate={isCollapsed && !isHovered ? "collapsed" : "expanded"}
          variants={collapsedVariants}
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
          style={{
            display: "flex",
            alignItems: "center",
            gap: 16,
            backgroundColor: "#FFFFFF",
            borderRadius: 40,
            boxShadow: '0 2px 10px rgba(0, 0, 0, 0.4)',
            padding: "8px 24px",
            flexWrap: "nowrap",
            overflow: "hidden",
            transition: "width 0.3s ease",
            fontFamily, // ensure font for all text inside motion div
          }}
        >
          <img
            src={Logo}
            alt="logo"
            width={40}
            height={40}
            style={{ cursor: isMobile ? "pointer" : "default" }}
            onClick={isMobile ? toggleDrawer(true) : undefined}
          />
          {isCollapsed && !isHovered && (
            <IconButton
              sx={{
                backgroundColor: "#1e2f2b",
                color: "white",
                width: 40,
                height: 40,
                "&:hover": { backgroundColor: "#1a2825" },
              }}
            >
              <AddIcon />
            </IconButton>
          )}
          {!isMobile && (!isCollapsed || isHovered) && (
            <Box sx={{ display: "flex", alignItems: "center", gap: 2, fontFamily }}>
              {menuItems.map((item, idx) => (
                <Typography
                  key={idx}
                  onClick={item.onClick}
                  sx={{
                    fontSize: { xs: "12px", sm: "14px", md: "15px" },
                    fontWeight: "bold",
                    color: "#333",
                    cursor: "pointer",
                    "&:hover": { color: "#0C2182" },
                    fontFamily,
                  }}
                >
                  {item.label}
                </Typography>
              ))}
            </Box>
          )}
        </motion.div>

        {/* Right Section */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 2,
            borderRadius: "40px",
            px: 3,
            py: 1.5,
            mt: { xs: 2, md: 0 },
            position: isScrolled ? "absolute" : "initial",
            top: isScrolled ? "10px" : "auto",
            right: isRTL ? "auto" : "10px", // Move right to left for RTL
            left: isRTL ? "10px" : "auto",  // Move left to right for RTL
            fontFamily,
          }}
        >
          {!isMobile ? (
            <>
              {isScrolled ? (
                <IconButton
                  sx={{ color: "#0C2182" }}
                  onClick={() =>
                    window.open("https://mail.google.com/mail/?view=cm&fs=1&to=ame@almansouryah.com", "_blank")
                  }
                >
                  <EmailIcon style={{ fontSize: "30px", paddingTop: "40px" }} />
                </IconButton>
              ) : (
                <>
                  <Button
                    onClick={handleLanguageMenuOpen}
                    sx={{
                      color: "white",
                      textTransform: "none",
                      fontWeight: "bold",
                      fontSize: "20px",
                      display: "flex",
                      alignItems: "center",
                      ml: isRTL ? 0 : 10, // Adjust margin for RTL
                      "&:hover": { color: "white", backgroundColor: "transparent" },
                      fontFamily,
                    }}
                  >
                    <LanguageIcon sx={{ mr: 0.5, fontSize: 25 }} />
                    {i18n.language === "ar" ? "العربية" : "English"}
                    <ArrowDropDownIcon sx={{ fontSize: 16 }} />
                  </Button>

                                      <Menu
                    anchorEl={langAnchorEl}
                    open={Boolean(langAnchorEl)}
                    onClose={handleLanguageMenuClose}
                    anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
                    transformOrigin={{ vertical: "top", horizontal: "left" }}
                    PaperProps={{ style: { fontFamily } }}
                  >
                    <MenuItem onClick={() => changeLanguage("ar")} selected={i18n.language === "ar"}>
                      العربية
                    </MenuItem>
                    <MenuItem onClick={() => changeLanguage("en")} selected={i18n.language === "en"}>
                      English
                    </MenuItem>
                  </Menu>
                </>
              )}
            </>
          ) : null}
        </Box>
      </Box>

      {/* Mobile Modal Drawer */}
      <Drawer
        anchor="top"
        open={drawerOpen}
        onClose={toggleDrawer(false)}
        sx={{
          "& .MuiDrawer-paper": {
            width: "90%",
            height: "100%",
            backgroundColor: "#f9f9f9",
            display: "flex",
            flexDirection: "column",
            padding: 2,
            fontFamily,
          },
        }}
      >
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <img src={Logo} alt="logo" width={40} height={40} />
          <IconButton onClick={toggleDrawer(false)}>
            <AddIcon sx={{ transform: "rotate(45deg)" }} />
          </IconButton>
        </Box>
        <Divider />
        <List sx={{ flexGrow: 1, overflowY: "auto" }}>
          {["en", "ar"].map((lang) => (
            <ListItem
              button
              key={lang}
              onClick={() => {
                changeLanguage(lang);
                toggleDrawer(false)();
              }}
              sx={{
                borderRadius: 1,
                mb: 1,
                bgcolor: i18n.language === lang ? "#e0e7ff" : "transparent",
                "&:hover": { bgcolor: "#e8eaf6" },
              }}
            >
              <ListItemText
                primary={lang === "en" ? "English" : "العربية"}
                sx={{
                  fontWeight: i18n.language === lang ? "bold" : "normal",
                  color: i18n.language === lang ? "#0C2182" : "#333",
                }}
              />
            </ListItem>
          ))}
        </List>
        <Divider sx={{ my: 2 }} />
        <List>
          {menuItems.map((item, index) => (
            <ListItem
              button
              key={index}
              onClick={() => {
                item.onClick();
                toggleDrawer(false)();
              }}
              sx={{
                borderRadius: 1,
                mb: 1,
                "&:hover": { backgroundColor: "#e8eaf6" },
              }}
            >
              <Box sx={{ color: "#0C2182", mr: isRTL ? 0 : 2, ml: isRTL ? 2 : 0 }}>{item.icon}</Box>
              <ListItemText
                primary={item.label}
                sx={{ fontWeight: "bold", color: "#0C2182", textAlign: isRTL ? "right" : "left" }}
              />
            </ListItem>
          ))}
        </List>
        <Box sx={{ p: 2, backgroundColor: "#e0e7ff", borderRadius: 2, m: 2 }}>
          <Button
            variant="contained"
            sx={{
              width: "100%",
              backgroundColor: "#0C2182",
              borderRadius: "40px",
              fontWeight: "bold",
              fontSize: "18px",
              "&:hover": { backgroundColor: "#091650" },
              textTransform: "none",
              fontFamily,
            }}
            endIcon={<EmailIcon />}
            onClick={() =>
              window.open("https://mail.google.com/mail/?view=cm&fs=1&to=ame@almansouryah.com", "_blank")
            }
          >
            {t("contactUs")}
          </Button>
        </Box>
      </Drawer>
    </Box>
  );
};

export default Navbar;

